import { useEffect, useRef } from 'react';

import { snackbar } from '@occc-app/design-system';
import { IPaginationProps } from '@occc/ui-design-system';
import { RetailerNetworkLocationFilter, RetailerNetworkLocationFragment } from '@occc/graphql';

import { useGetRetailerNetworkLocations } from '../../../data-access/queries';
import { useRetailerNetworkLocationGroupRemoveLocationsMutation } from '../../../data-access/mutations';
import {
  ConnectionStatus,
  toConnectionStatusEquals,
} from '../retailer-network-my-stores-v2-page.types';

export const OTHERS_GROUP_ID = '__others__';
const STORES_PER_PAGE = 8;

type UseLocationGroupStoresOptions = {
  groupUuid: string;
  networkId: string;
  allGroupUuids?: string[];
  stateFilter?: string;
  cityFilter?: string;
  connectionStatusFilter?: ConnectionStatus | '';
  skip?: boolean;
};

type UseLocationGroupStoresResult = {
  displayedLocations: RetailerNetworkLocationFragment[];
  isInitialLoading: boolean;
  loading: boolean;
  paginationProps: IPaginationProps;
  isRemoveInProgress: boolean;
  handleRemoveFromLocationGroup: (locationUuid: string) => Promise<void>;
};

export const useLocationGroupStores = ({
  groupUuid,
  networkId,
  allGroupUuids,
  stateFilter,
  cityFilter,
  connectionStatusFilter,
  skip = false,
}: UseLocationGroupStoresOptions): UseLocationGroupStoresResult => {
  const isOthers = groupUuid === OTHERS_GROUP_ID;

  const filter: RetailerNetworkLocationFilter = {
    retailerNetworkUuidEquals: networkId,
    stateEquals: stateFilter || undefined,
    cityEquals: cityFilter || undefined,
    connectionStatusEquals: toConnectionStatusEquals(connectionStatusFilter || ''),
    ...(isOthers
      ? allGroupUuids?.length
        ? { retailerNetworkLocationGroupUuidNotIn: allGroupUuids }
        : {}
      : { retailerNetworkLocationGroupUuidContains: [groupUuid] }),
  };

  const {
    data: locations = [],
    paginationProps,
    loading,
  } = useGetRetailerNetworkLocations({
    filter,
    pageSize: STORES_PER_PAGE,
    baseOptions: {
      skip,
      onError: () => snackbar.error('Error loading stores for this group'),
    },
  });

  // Keep the last successfully loaded page visible while a new page is loading
  // so the grid height never changes during pagination.
  const stableLocationsRef = useRef<RetailerNetworkLocationFragment[]>([]);
  useEffect(() => {
    if (!loading) {
      stableLocationsRef.current = locations;
    }
  }, [loading, locations]);

  const displayedLocations =
    loading && stableLocationsRef.current.length > 0 ? stableLocationsRef.current : locations;
  const isInitialLoading = loading && stableLocationsRef.current.length === 0;

  const [removeLocations, { loading: isRemoveInProgress }] =
    useRetailerNetworkLocationGroupRemoveLocationsMutation({
      onCompleted: () => snackbar.success('Location successfully removed from the Location Group'),
      onError: () => snackbar.error('Error removing Location from the Location Group'),
      refetchQueries: [
        'retailerNetworkLocations',
        'retailerNetworkLocationGroups',
        'retailerNetworkLocationGroupOnlineStatus',
      ],
      awaitRefetchQueries: true,
    });

  const handleRemoveFromLocationGroup = async (locationUuid: string) => {
    await removeLocations({
      variables: {
        retailerNetworkUuid: networkId,
        locationGroupUuid: groupUuid,
        locationUuids: [locationUuid],
      },
    });
  };

  return {
    displayedLocations,
    isInitialLoading,
    loading,
    paginationProps,
    isRemoveInProgress,
    handleRemoveFromLocationGroup,
  };
};
